import React, { useEffect, useMemo, useState } from "react";
import Calendar from "../Common/Calendar";
import TimePickerValue from "../Common/TimePicker";
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getAPI, postAPI } from "../api/callAPI";
import moment from "moment";
import { isArray } from "lodash";

const slotStartKey = (value) => {
  const raw = `${value || ""}`.split("-")[0].trim();
  const parsed = moment(raw, ["HH:mm:ss", "HH:mm", "h:mm A", "h:mmA"], true);
  return parsed.isValid() ? parsed.format("HH:mm") : raw;
};

const formatSlotTime = (value) => {
  const key = slotStartKey(value);
  const parsed = moment(key, "HH:mm", true);
  return parsed.isValid() ? parsed.format("h:mm A") : "this time";
};

const DateTimeWrapper = (Props) => {
  const [isLoading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotError, setSlotError] = useState(null);
  const [bookingOffError, setBookingOffError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [closedSlots, setClosedSlots] = useState(null);
  // NEW: For availability check
  const [availabilityError, setAvailabilityError] = useState(null);
  const [isSlotAvailable, setIsSlotAvailable] = useState(true); // NEW: For button disabling
  const [seatingPreferences, setSeatingPreferences] = useState([]);
  const [loadingSeating, setLoadingSeating] = useState(false);
  const [guestCapError, setGuestCapError] = useState(null);
  const MAX_GUESTS = 30;
  const st = useSelector((state) => state.auth);
  // const dispath = useDispatch();
  const { handleData, storeID, liveEvent } = Props;

  const store_id = useMemo(() => {
    return parseInt(storeID, 10);
  }, [storeID]);
  const Count = Array.from({ length: 31 }, (_, i) => i);
  const [Data, setData] = useState({
    Time: "",
    Date: "",
    Adults: 0,
    Child: 0,
    Infants: 0,
    SeatingPreference: "",
  });

  const dateText = useMemo(() => {
    if (liveEvent) {
      return `${moment(liveEvent?.event_date).format(
        "DD MMM[,] YYYY"
      )}, ${moment(liveEvent?.time, "HH:mm:ss").format("h:mm a")} onwards`;
    }
    return "";
  }, [liveEvent]);

  useEffect(() => {
    if (liveEvent) {
      const obj = {
        Adults: 0,
        Child: 0,
        Infants: 0,
        Date: moment(liveEvent?.event_date).format("YYYY-MM-DD"),
        Time: moment(liveEvent?.time, "HH:mm:ss").format("HH:mm"),
      };

      // console.log("live event set data", obj);
      setData(obj);
    }
  }, [liveEvent]);
  useEffect(() => {
    const fetchSeatingPreferences = async () => {
      if (!store_id) return;
      setLoadingSeating(true);
      try {
        const resp = await getAPI(st?.endpoint, `seating-preferences/${store_id}`);
        if (resp?.success && isArray(resp?.data) && resp.data.length > 0) {
          const preferences = resp.data.map((ele) => ({
            label: ele.title,
            value: ele.title,
          }));
          setSeatingPreferences(preferences);
        }
      } catch (error) {
        console.log("seating preferences fetch failed:", error);
      } finally {
        setLoadingSeating(false);
      }
    };
    fetchSeatingPreferences();
  }, [store_id, st?.endpoint]);
  useEffect(() => {
    if (!loadingSeating && seatingPreferences.length === 0 && !Data.SeatingPreference) {
      setData((prev) => ({ ...prev, SeatingPreference: "Standard" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatingPreferences, loadingSeating]);

  // NEW: Check availability when time or guest count changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (!Data.Time || !Data.Date || (Data.Adults + Data.Child + Data.Infants) === 0) {
        setAvailabilityError(null);
        setIsSlotAvailable(true); // Allow to proceed when no selection made
        return;
      }

      try {
        // eslint-disable-next-line eqeqeq
        const weekday = dayjs(Data.Date).format("d") != 0 ? dayjs(Data.Date).format("d") : 7;
        const requestData = {
          store_id: store_id,
          type: 2,
          weekday: Number(weekday),
          date: Data.Date,
        };

        // Call get-slots-with-covers API
        const response = await postAPI(st?.endpoint, "get-slots-with-covers", requestData);
        
        if (response?.success && Array.isArray(response?.data?.slots)) {
          const selectedSlot = response.data.slots.find(
            (slot) => slotStartKey(slot.slot) === slotStartKey(Data.Time)
          );
          const totalGuests = Data.Adults + Data.Child + Data.Infants;
          
          const timeLabel = formatSlotTime(Data.Time);
          const seatsLeft = Number(selectedSlot?.available);

          if (selectedSlot) {
            if (selectedSlot.status === false || seatsLeft <= 0) {
              setAvailabilityError(
                `${timeLabel} is fully booked. Please choose another time.`
              );
              setIsSlotAvailable(false);
            } else if (seatsLeft < totalGuests) {
              const seatWord = seatsLeft === 1 ? "seat" : "seats";
              const guestWord = totalGuests === 1 ? "guest" : "guests";
              setAvailabilityError(
                `Only ${seatsLeft} ${seatWord} left at ${timeLabel}, which isn’t enough for ${totalGuests} ${guestWord}. Choose another time or a smaller party. We recommend contacting the restaurant directly.`
              );
              setIsSlotAvailable(false);
            } else {
              setAvailabilityError(null);
              setIsSlotAvailable(true);
            }
          } else {
            setAvailabilityError(
              `${timeLabel} is no longer open. Please choose another time.`
            );
            setIsSlotAvailable(false);
          }
        } else {
          setAvailabilityError(
            "We couldn’t check if this time is still open. Please try again."
          );
          setIsSlotAvailable(false);
        }
      } catch (error) {
        setAvailabilityError(
          "We couldn’t check if this time is still open. Please try again."
        );
        setIsSlotAvailable(false);
      }
    };

    checkAvailability();
  }, [Data.Time, Data.Date, Data.Adults, Data.Child, Data.Infants, store_id, st?.endpoint]);

  // Pass availability status to parent component
  useEffect(() => {
    // Pass both Data and availability status to parent
    handleData({
      ...Data,
      isSlotAvailable: isSlotAvailable // NEW: Add availability flag
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Data, isSlotAvailable]);

  // useEffect(()=>{
  //   handleDateChange(new Date())
  // },[])

  // useEffect(()=>{
  //   if(st?.endpoint!= 'kalimirchi-dev') handleDateChange(dayjs(new Date()))
  // },[st?.endpoint])

  const getBokingOff = async (date) => {
    setBookingOffError(null);
    try {
      const Data = {
        store_id: store_id,
        date,
      };
      const Resp = await postAPI(st?.endpoint, "booking-off", Data);
      let fetchSlots = true;
      if (Resp?.success) {
        if (Resp?.data?.id) {
          if (isArray(Resp?.data?.slots) && Resp?.data?.slots.length > 0) {
            fetchSlots = true;
            setClosedSlots(Resp?.data?.slots);
          } else if (Resp?.data?.msg) {
            fetchSlots = false;
            setBookingOffError(Resp?.data?.msg);
          }

          //           slots
          // :
          // ["12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00"]
        }
      }
      return fetchSlots;
    } catch {
      setBookingOffError("Something went wrong. Please try again.");
      return false;
      // setSlots(["No Data Found"]);
    }
  };

  const APICall = async (Weekday, Date) => {
    const Data = {
      store_id: store_id,
      type: 2,
      weekday: Number(Weekday),
      date: Date,
    };
    setSlotError(null);
    try {
      const Resp = await postAPI(st?.endpoint, "get-slots/", Data);
      if (Resp?.data?.slots?.length > 0) {
        setSlots(Resp?.data?.slots);
      } else {
        setSlots([]);
        setSlotError("No booking times are open on this date. Please try another date.");
      }
    } catch (err) {
      setSlots([]);
      setSlotError(err?.response?.data?.message || "Something went wrong. Please try again.");
    }
  };

  const handleChange = (e, t) => {
    // console.log("e?.target?.value", e?.target?.value);
    const newValue = e?.target?.value;
    const adults = t === "Adults" ? newValue : Data.Adults;
    const child = t === "Child" ? newValue : Data.Child;
    const infants = t === "Infants" ? newValue : Data.Infants;
    const total = Number(adults) + Number(child) + Number(infants);

    if ((t === "Adults" || t === "Child" || t === "Infants") && total > MAX_GUESTS) {
      setGuestCapError(`Maximum ${MAX_GUESTS} guests allowed per booking.`);
      return;
    }
    setGuestCapError(null);
    setData({ ...Data, [t]: newValue });
  };
  const handleTimeChange = (T) => {
    setData({ ...Data, Time: T });
  };
  const handleDateChange = async (D) => {
    // eslint-disable-next-line eqeqeq
    const WEEKDAY = dayjs(D).format("d") != 0 ? dayjs(D).format("d") : 7;
    const DATE = dayjs(D).format("YYYY-MM-DD");
    setLoading(true);
    // NEW: Clear previous availability error when date changes
    setAvailabilityError(null);
    setIsSlotAvailable(true);
    setGuestCapError(null);
    try {
      const fetchSlots = await getBokingOff(DATE);
      if (fetchSlots) {
        await APICall(WEEKDAY, DATE);
        setData((prev) => ({ ...prev, Date: DATE, Time: "" }));
      } else {
        setSlotError(null);
        setSlots([]);
        setData((prev) => ({ ...prev, Date: "", Time: "" }));
      }
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  if (liveEvent) {
    return (
      <div className="row d-flex">
        <React.Fragment>
          <Typography
            // component="h2"
            variant="h5"
            align="center"
            color={"black"}
            fontStyle={"bold"}
          >
            {"Live Event Details"}
          </Typography>
          <Typography variant="body1" align="center" color={"primary"}>
            {liveEvent.name}
          </Typography>

          <Typography variant="body1" align="center" color={"primary"} mb={3}>
            {dateText}
          </Typography>
          {/* <InputLabel id="party-size-hint" sx={{ whiteSpace: "normal" }}>
            If Party of more than 30 people, please write in Special Requests
          </InputLabel> */}
          {guestCapError && (
            <Alert severity="warning" sx={{ my: 2 }}>
              {guestCapError}
            </Alert>
          )}
          <Box
            sx={{
              mt: 2,
              mx: "auto",
              width: { xs: "100%", sm: "50%" },
              maxWidth: 360,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="Adults-ddown">Adults</InputLabel>
              <Select
                id="Adults-ddown"
                labelId="Adults-ddown"
                value={Data.Adults}
                onChange={(e) => {
                  handleChange(e, "Adults");
                }}
                label="Adults"
              >
                {Count.map((num, index) => (
                  <MenuItem value={num} key={index}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="Child-ddown">Child</InputLabel>
              <Select
                id="Child-ddown"
                labelId="Child-ddown"
                value={Data.Child}
                onChange={(e) => {
                  handleChange(e, "Child");
                }}
                label="Child"
              >
                {Count.map((num, index) => (
                  <MenuItem value={num} key={index}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="Infants-ddown">Infants</InputLabel>
              <Select
                id="Infants-ddown"
                labelId="Infants-ddown"
                value={Data.Infants}
                onChange={(e) => {
                  handleChange(e, "Infants");
                }}
                label="Infants"
              >
                {Count.map((num, index) => (
                  <MenuItem value={num} key={index}>
                    {num}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="Seating-ddown">Seating Area</InputLabel>
              <Select
                id="Seating-ddown"
                labelId="Seating-ddown"
                value={Data.SeatingPreference}
                onChange={(e) => {
                  handleChange(e, "SeatingPreference");
                }}
                label="Seating Area"
                disabled={loadingSeating || seatingPreferences.length === 0}
              >
                {seatingPreferences.map((option, index) => (
                  <MenuItem value={option.value} key={index}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </React.Fragment>
      </div>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          overflowX: "auto",
          width: "100%",
          "& .MuiDateCalendar-root": {
            maxWidth: "100%",
            width: { xs: "100%", sm: 320 },
          },
          "& .MuiPickersSlideTransition-root": {
            minHeight: { xs: 200, sm: 242 },
          },
        }}
      >
        <Calendar handleDate={handleDateChange} />
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress variant="indeterminate" />
        </Box>
      ) : (
        <>
          {slotError ? (
            <InputLabel color="error" id="slot-error-message">
              {slotError}
            </InputLabel>
          ) : null}

          {bookingOffError ? (
            <InputLabel color="error" id="booking-off-error">
              {bookingOffError}
            </InputLabel>
          ) : null}

          {availabilityError && (
            <Alert severity="error" sx={{ my: 2 }}>
              {availabilityError}
            </Alert>
          )}

          {guestCapError && (
            <Alert severity="warning" sx={{ my: 2 }}>
              {guestCapError}
            </Alert>
          )}

          {st?.userdata?.Date && slots && slots.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {/* <InputLabel id="party-size-hint" sx={{ mb: 1, whiteSpace: "normal" }}>
                If Party of more than 30 people, please write in Special Requests
              </InputLabel> */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: 360 }}>
                  <TimePickerValue handleTime={handleTimeChange} slots={slots} />
                </Box>
                <Box
                  sx={{
                    width: { xs: "100%", sm: "50%" },
                    maxWidth: 360,
                    mt: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <FormControl fullWidth>
                    <InputLabel id="Adults-ddown">Adults</InputLabel>
                    <Select
                      id="Adults-ddown"
                      labelId="Adults-ddown"
                      value={Data.Adults}
                      onChange={(e) => {
                        handleChange(e, "Adults");
                      }}
                      label="Adults"
                    >
                      {Count.map((num, index) => (
                        <MenuItem value={num} key={index}>
                          {num}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="Child-ddown">Child</InputLabel>
                    <Select
                      id="Child-ddown"
                      labelId="Child-ddown"
                      value={Data.Child}
                      onChange={(e) => {
                        handleChange(e, "Child");
                      }}
                      label="Child"
                    >
                      {Count.map((num, index) => (
                        <MenuItem value={num} key={index}>
                          {num}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="Infants-ddown">Infants</InputLabel>
                    <Select
                      id="Infants-ddown"
                      labelId="Infants-ddown"
                      value={Data.Infants}
                      onChange={(e) => {
                        handleChange(e, "Infants");
                      }}
                      label="Infants"
                    >
                      {Count.map((num, index) => (
                        <MenuItem value={num} key={index}>
                          {num}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
  
                  <FormControl fullWidth>
                    <InputLabel id="Seating-ddown">Seating Area</InputLabel>
                    <Select
                      id="Seating-ddown"
                      labelId="Seating-ddown"
                      value={Data.SeatingPreference}
                      onChange={(e) => {
                        handleChange(e, "SeatingPreference");
                      }}
                      label="Seating Area"
                      disabled={loadingSeating || seatingPreferences.length === 0}
                    >
                      {seatingPreferences.map((option, index) => (
                        <MenuItem value={option.value} key={index}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default DateTimeWrapper;
